import { ajvResolver } from '@hookform/resolvers/ajv';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import { useEditComponentPageContext } from 'src/components/context/components/EditComponentPageContext';
import { Drawer } from 'src/components/drawer';
import { SidebarHeader } from 'src/components/header';
import {
  SelectDropdownWidget,
  SwitchButtonWidget,
} from 'src/components/widget';
import { ResourceSelectorSidebar } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import { useGetThemes } from 'src/hooks';
import {
  ShelfPatternEnum,
  ShelfPositionEnum,
  ShelfProductPatternEnum,
  ShelfStyleEnum,
} from 'src/interfaces';
import { lang } from 'src/utils';
import { handleComponentConfigChange } from 'src/utils/changeHandler';

import { InfoBox } from '../../modules/InfoBox';
import { useThemeOptions } from '../FormSelector.hook';
import { CommonFormProps } from '../FormSelector.interface';
import { FormWrapper } from '../FormWrapper';
import { HeaderTextStylingAccordion } from '../modules/headerTextStylingAccordion/HeaderTextStylingAccordion';
import { HeaderTextStylingOptions } from '../modules/headerTextStylingAccordion/HeaderTextStylingAccordion.interface';
import { ProductsDrawer } from '../modules/productsDrawer';
import { ProductsSourceController } from '../modules/productsSourceController/ProductsSourceController';
import { DyTabs } from './modules/dyTabs/DyTabs';
import { MobileUserAccordion } from './modules/mobileUserAccordion/MobileUserAccordion';
import { TextAlignmentForm } from './modules/textAlignmentForm/TextAlignmentForm';
import {
  ISingleShelfConfig,
  SingleShelfConfigSchema,
} from './SingleShelf.interface';
import { useSingleShelfForm } from './SingleShelfForm.hook';
import { SidebarContainer, SidebarContent } from './SingleShelfForm.styled';

export const SingleShelfForm = (props: CommonFormProps) => {
  const {
    control,
    handleSubmit: submitWrapper,
    reset,
    watch,
    formState,
    getValues,
  } = useForm<ISingleShelfConfig>({
    values: {
      mop: {
        background: props.componentDetails?.config?.mop?.background || '',
      },
      ...props.componentDetails?.config,
    },
    resolver: ajvResolver(
      SingleShelfConfigSchema as unknown as JSONSchemaType<ISingleShelfConfig>,
    ),
  });

  // show/hide fields conditionally depending on form/prop values
  const showLineupStyle = watch('lineupStyle.type') === 'STAGGERED';

  const {
    openBackgroundDrawer,
    openTitleDrawer,
    openSettingsDrawer,
    openProductsDrawer,
    onToggleBackgroundDrawer,
    onToggleTitleDrawer,
    onToggleSettingsDrawer,
    onToggleProductsDrawer,
  } = useSingleShelfForm();

  const { data: themes = [] } = useGetThemes();
  const { handleChangeComponentDetails } = useEditComponentPageContext();
  const themeOptions = useThemeOptions();

  const handleOnChange = (
    key: string | string[],
    value: unknown,
    fieldOnChange?: any,
  ) => {
    props.onChange(props.componentDetails.config ?? {}, key, value);
    fieldOnChange && fieldOnChange(value);
  };

  const handleSubmit: SubmitHandler<ISingleShelfConfig> = (
    value: typeof props.componentDetails.config,
  ) => {
    props.onSave(value);
  };

  const headerTextStyleHasErrors = () => {
    if (
      formState.errors.header ||
      formState.errors.disclaimer ||
      formState.errors.productList?.config?.sizeLabel ||
      formState.errors.productList?.config?.sizes
    )
      return true;
    else return false;
  };

  const settingsHasErrors = () => {
    if (formState.errors.lineupStyle) return true;
    else return false;
  };

  const productsHasErrors = () => {
    return Boolean(formState.errors.productList);
  };

  const handleThemeChange = (themeId: unknown) => {
    const theme = themes.find((theme) => theme.id === themeId);

    if (theme) {
      handleChangeComponentDetails([themeId, theme], ['theme_id', 'theme']);
    }
  };
  const hasBackgroundImage = (mop?: { background?: string }) =>
    typeof mop?.background === 'string' && mop.background.trim() !== '';

  const items = [
    {
      label: lang.componentSettings,
      onLabelClick: onToggleSettingsDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: settingsHasErrors(),
    },
    {
      label: lang.background,
      onLabelClick: onToggleBackgroundDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: props.hasTriedSaving
        ? !hasBackgroundImage(props.componentDetails.config)
        : false,
    },
    {
      label: lang.headerTextStyling,
      onLabelClick: onToggleTitleDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: headerTextStyleHasErrors(),
    },
    {
      label: lang.products,
      onLabelClick: onToggleProductsDrawer,
      content: <div />,
      disabled: props.componentDetails?.config?.productsSource === 'DYS',
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: productsHasErrors(),
    },
  ];

  const isResourceSelectorDisabled =
    openBackgroundDrawer ||
    openTitleDrawer ||
    openSettingsDrawer ||
    openProductsDrawer;

  return (
    <FormWrapper
      //  form={props.form}
      formState={formState}
      formReset={reset}
      formGetValues={getValues}
      onSave={submitWrapper(handleSubmit)}>
      <ProductsSourceController
        componentDetails={props.componentDetails}
        handleChange={handleOnChange}
        disabled={isResourceSelectorDisabled}
      />
      <DyTabs
        productsSource={props.componentDetails?.config?.productsSource}
        layoutTypeName={props.componentDetails.layoutType?.name}
        handleOnChange={handleOnChange}
        panels={{
          default: <SidebarAccordion items={items} />,
          mobile: (
            <MobileUserAccordion
              mop={getValues('mop')}
              formState={formState}
              handleOnChange={handleOnChange}
              hasTriedSaving={props.hasTriedSaving}
              componentDetails={props.componentDetails}
            />
          ),
        }}
      />

      <Controller
        name="background"
        control={control}
        render={({ field, fieldState }) => (
          <ResourceSelectorSidebar
            label={lang.background}
            type={ResourceType.BACKGROUND}
            defaultAssetName={field.value}
            openResourceSelectorDrawer={openBackgroundDrawer}
            onToggleResourceSelectorDrawer={onToggleBackgroundDrawer}
            onChange={(value) => {
              handleOnChange('background', value);
            }}
            hasError={!!fieldState.error}
            error={fieldState.error?.message || false}
          />
        )}
      />
      <Drawer open={openTitleDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.headerTextStyling}
            onBackClick={onToggleTitleDrawer}
          />
          <SidebarContent>
            <HeaderTextStylingAccordion
              componentConfig={props.componentDetails.config}
              handleOnChange={handleOnChange}
              errors={formState?.errors}
              theme={props.componentDetails.theme}
              hidden={[
                HeaderTextStylingOptions.SUBHEADER,
                HeaderTextStylingOptions.HEADER_LAYOUT,
                HeaderTextStylingOptions.MOLECULE_DISPLAY_LABEL,
              ]}
            />
          </SidebarContent>
        </SidebarContainer>
      </Drawer>
      <Drawer open={openSettingsDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.componentSettings}
            onBackClick={onToggleSettingsDrawer}
          />
          <SidebarContent>
            <Grid container spacing={2} style={{ padding: '26px' }}>
              <Grid item xs={12}>
                <Controller
                  name="showComponentProductDisclaimer"
                  control={control}
                  defaultValue={false}
                  render={({ field: { ref, ...field } }) => (
                    <SwitchButtonWidget
                      {...field}
                      label={lang.showProductDisclaimers}
                      ref={ref}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          'showComponentProductDisclaimer',
                          value,
                          field.onChange,
                        )
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectDropdownWidget
                  label={lang.selectTheme}
                  value={props.componentDetails.theme_id}
                  selectOptions={themeOptions}
                  onChange={handleThemeChange}
                />

                <InfoBox>
                  If you change the theme, please review the default assets are
                  correct for existing products.
                </InfoBox>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="lineupStyle.type"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <SelectDropdownWidget
                      {...field}
                      ref={ref}
                      hasError={!!fieldState.error}
                      error={fieldState.error?.message || false}
                      label={lang.lineupStyle}
                      selectOptions={[
                        {
                          label: lang.staggered,
                          value: ShelfStyleEnum.STAGGERED,
                        },
                        {
                          label: lang.straight,
                          value: ShelfStyleEnum.STRAIGHT,
                        },
                      ]}
                      onChange={(value) => {
                        handleComponentConfigChange(
                          props,
                          'lineupStyle.type',
                          value,
                          field.onChange,
                        );
                      }}
                    />
                  )}
                />
              </Grid>

              {showLineupStyle && (
                <Grid item xs={12}>
                  <Controller
                    name="lineupStyle.pattern"
                    control={control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <SelectDropdownWidget
                        {...field}
                        ref={ref}
                        hasError={!!fieldState.error}
                        error={fieldState.error?.message || false}
                        label={lang.pattern}
                        selectOptions={[
                          {
                            label: lang.firstProductTop,
                            value: ShelfPatternEnum.FROM_TOP,
                          },
                          {
                            label: lang.firstProductBottom,
                            value: ShelfPatternEnum.FROM_BOTTOM,
                          },
                        ]}
                        onChange={(value) =>
                          handleComponentConfigChange(
                            props,
                            'lineupStyle.pattern',
                            value,
                            field.onChange,
                          )
                        }
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Controller
                  name="lineupStyle.position"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <SelectDropdownWidget
                      {...field}
                      ref={ref}
                      hasError={!!fieldState.error}
                      error={fieldState.error?.message || false}
                      label={lang.position}
                      selectOptions={[
                        { label: lang.center, value: ShelfPositionEnum.CENTER },
                        {
                          label: lang.bottom,
                          value: ShelfPositionEnum.BOTTOM,
                        },
                      ]}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          'lineupStyle.position',
                          value,
                          field.onChange,
                        )
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="lineupStyle.order"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <SelectDropdownWidget
                      {...field}
                      ref={ref}
                      hasError={!!fieldState.error}
                      error={fieldState.error?.message || false}
                      label={lang.order}
                      selectOptions={[
                        {
                          label: lang.default,
                          value: ShelfProductPatternEnum.DEFAULT,
                        },
                        {
                          label: lang.imageBelowText,
                          value: ShelfProductPatternEnum.IMAGE_BELOW_TEXT,
                        },
                      ]}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          'lineupStyle.order',
                          value,
                          field.onChange,
                        )
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextAlignmentForm
                  control={control}
                  props={props}
                  getValues={getValues}
                />
              </Grid>
            </Grid>
          </SidebarContent>
        </SidebarContainer>
      </Drawer>
      <ProductsDrawer
        open={openProductsDrawer}
        componentDetails={props.componentDetails}
        onClose={onToggleProductsDrawer}
        handleOnChange={handleOnChange}
      />
    </FormWrapper>
  );
};
